import { z } from "zod";
import { TABLE_CALL_TYPES, bellOptionsOf, type DiningTable, type TableCall } from "../types";
import { audit, fail, getSettings, newId, parse, requirePermission, type Actor, type Ctx } from "./context";

/**
 * QR table bell. Each table's QR code carries a secret key; a customer who scans it while the table has an open
 * dine-in bill can call staff. The public server runs `bell.call` as this fixed actor, which can do nothing else.
 */
export const BELL_ACTOR: Actor = { id: "table-bell", name: "Table bell", role: "kitchen", permissions: [] };

/** Presses per bill per minute; more are answered "staff already notified" and not stored. */
export const BELL_CALLS_PER_MINUTE = 2;
const Id = z.string().min(1).max(128);

/** 32 random URL-safe characters. */
export function newBellKey(): string {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
  return Array.from(crypto.getRandomValues(new Uint8Array(32)), (byte) => alphabet[byte % 64]).join("");
}

/** Keeps a customer's note to one short line of plain text. */
export function cleanBellNote(note: string): string {
  return note.replace(/[\u0000-\u001f\u007f<>]/g, " ").replace(/\s+/g, " ").trim().slice(0, 140);
}

async function tableOf(ctx: Ctx, id: string): Promise<DiningTable> {
  return await ctx.tx.get("tables", id) ?? fail("not-found", "Table not found.");
}

/** Creates or replaces the table's QR key. The old printed QR code stops working at once. */
export async function newTableQr(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "tables.manage");
  const input = parse(z.object({ tableId: Id }), raw);
  const table = await tableOf(ctx, input.tableId);
  ctx.tx.set("tables", { ...table, bellKey: newBellKey() });
  audit(ctx, table.bellKey ? "table.qr_replaced" : "table.qr_created", "table", table.id, { name: table.name });
  return { id: table.id };
}

export async function setBellMuted(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ tableId: Id, muted: z.boolean() }), raw);
  const table = await tableOf(ctx, input.tableId);
  ctx.tx.set("tables", { ...table, bellMuted: input.muted });
  audit(ctx, input.muted ? "table.bell_muted" : "table.bell_unmuted", "table", table.id, { name: table.name });
  return { id: table.id };
}

/**
 * A customer's bell press, sent by the public server after it checked the phone's QR session. The bill named in
 * the session must still be the table's open bill — a phone from an earlier, paid bill cannot ring.
 */
export async function callFromTable(ctx: Ctx, raw: unknown): Promise<{ status: "sent" | "throttled" | "muted" | "ended" | "pending" }> {
  if (ctx.actor.id !== BELL_ACTOR.id) fail("permission-denied", "Only the table bell can do this.");
  // `buttonId` names the pressed button; a page loaded before buttons could be named sends only `type`.
  const input = parse(z.object({ tableId: Id, orderId: Id, buttonId: z.string().max(64).optional(), type: z.enum(TABLE_CALL_TYPES).optional(), note: z.string().max(500).default("") }), raw);
  const table = await ctx.tx.get("tables", input.tableId);
  if (!table || !table.active || !table.activeOrderId || table.activeOrderId !== input.orderId) return { status: "ended" };
  const order = await ctx.tx.get("orders", input.orderId);
  if (!order || order.status !== "open") return { status: "ended" };
  if (table.bellMuted) return { status: "muted" };
  // A button the manager took off the QR page cannot be pressed by an old copy of the page either.
  const { buttons } = bellOptionsOf(await getSettings(ctx.tx));
  const button = buttons.find((entry) => input.buttonId ? entry.id === input.buttonId : entry.kind === input.type) ?? fail("invalid-argument", "This request is not available here.");
  const previous = await ctx.tx.query("tableCalls", [{ field: "orderId", op: "==", value: order.id }]);
  // The same request still waiting is not repeated: the customer is told staff already have it.
  if (button.kind !== "other" && previous.some((call) => (call.buttonId ?? call.type) === button.id && call.status !== "done")) return { status: "pending" };
  const recent = previous.filter((call) => ctx.now.getTime() - Date.parse(call.createdAt) < 60_000);
  if (recent.length >= BELL_CALLS_PER_MINUTE) return { status: "throttled" };
  const call: TableCall = {
    id: newId(), tableId: table.id, tableName: table.name, orderId: order.id, dailyNumber: order.dailyNumber, type: button.kind, buttonId: button.id, label: button.label,
    note: button.kind === "other" ? cleanBellNote(input.note) : "",
    status: "open", createdAt: ctx.iso, doneAt: null, doneBy: null, businessDate: ctx.businessDate, schemaVersion: 1,
  };
  ctx.tx.set("tableCalls", call);
  return { status: "sent" };
}

/** A staff member saw the call and is on the way; the customer's phone says so. */
export async function receiveCall(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ callId: Id }), raw);
  const call = await ctx.tx.get("tableCalls", input.callId) ?? fail("not-found", "Call not found.");
  if (call.status === "open") ctx.tx.set("tableCalls", { ...call, status: "received", receivedAt: ctx.iso, receivedBy: ctx.actor.name });
  return { id: call.id };
}

export async function finishCall(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ callId: Id }), raw);
  const call = await ctx.tx.get("tableCalls", input.callId) ?? fail("not-found", "Call not found.");
  if (call.status !== "done") ctx.tx.set("tableCalls", { ...call, status: "done", doneAt: ctx.iso, doneBy: ctx.actor.name, receivedAt: call.receivedAt ?? ctx.iso, receivedBy: call.receivedBy ?? ctx.actor.name });
  return { id: call.id };
}

/** What the table QR page offers customers. */
export async function setBellOptions(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "tables.manage");
  const Button = z.object({
    id: z.string().max(64).optional(), kind: z.enum(TABLE_CALL_TYPES), label: z.string().transform(cleanBellNote).pipe(z.string().min(1, "Every button needs a name").max(30)),
    labelSi: z.string().transform(cleanBellNote).pipe(z.string().max(40)).default(""), icon: z.string().trim().max(8).default("")
  });
  const input = parse(z.object({ buttons: z.array(Button).min(1, "Keep at least one button").max(8, "At most 8 buttons"), showBill: z.boolean(), showMenu: z.boolean() }), raw);
  // A new button gets its id here; an existing one keeps it, so calls already made still point at it.
  const options = { ...input, buttons: input.buttons.map((button) => ({ ...button, id: button.id || newId() })) };
  ctx.tx.set("settings", { ...await getSettings(ctx.tx), bellOptions: options, updatedAt: ctx.iso });
  audit(ctx, "table.bell_options", "settings", "restaurant", options);
  return options;
}
